/**
 * Pallet Group Assignment API client.
 * UI actions call these functions; swap mock implementations for HTTP once endpoints exist.
 */

import {
  MOCK_UNASSIGNED_PALLET_GROUPS,
  MOCK_SPUR_SUMMARY,
} from "../data/mockPalletGroupData";
import { resolveIlpnIdsForRow } from "../components/PalletGroup/pgIlpnUtils";
import {
  applyAssignPalletGroupsToSpurs,
  applyReassignToSpurs,
  applyUnassignFromSpur,
  createInitialSpurAssignments,
  getAssignedPalletGroupsForSpur,
} from "../components/PalletGroup/pgSpurAssignmentUtils";

function cloneWorkspaceState(state) {
  return {
    unassignedPalletGroups: [...(state.unassignedPalletGroups || [])],
    spurSummary: [...(state.spurSummary || [])],
    spurAssignments: JSON.parse(JSON.stringify(state.spurAssignments || {})),
  };
}

/**
 * @typedef {object} AssignmentWorkspace
 * @property {Array} unassignedPalletGroups
 * @property {Array} spurSummary
 * @property {Record<string, Array>} spurAssignments
 */

/**
 * Load unassigned PGs, spur summary, and spur assignment map.
 * @param {{ siteCode?: string }} [params]
 * @returns {Promise<AssignmentWorkspace>}
 */
export async function fetchAssignmentWorkspace(params = {}) {
  // TODO: Replace with GET /inbound-planning/{siteCode}/pallet-group-assignment/workspace
  const { siteCode } = params;
  void siteCode;

  return {
    unassignedPalletGroups: [...MOCK_UNASSIGNED_PALLET_GROUPS],
    spurSummary: [...MOCK_SPUR_SUMMARY],
    spurAssignments: createInitialSpurAssignments(),
  };
}

/**
 * @param {{ siteCode?: string, spurId: string, spurRow: object, spurAssignments: object }} params
 */
export async function fetchSpurAssignedPalletGroups(params) {
  // TODO: Replace with GET /inbound-planning/spurs/{spurId}/pallet-groups
  const { spurId, spurRow, spurAssignments } = params;
  void spurId;

  return getAssignedPalletGroupsForSpur(spurRow, spurAssignments);
}

/**
 * @param {{ palletGroupId: string, palletGroupRow: object, includeCartonStatus?: boolean }} params
 */
export async function fetchPalletGroupIlpns(params) {
  // TODO: Replace with GET /inbound-planning/pallet-groups/{palletGroupId}/ilpns
  const { palletGroupId, palletGroupRow, includeCartonStatus = false } = params;
  void palletGroupId;

  const row = palletGroupRow || {};
  const ilpnIds = resolveIlpnIdsForRow(row);

  return {
    ilpnIds,
    diverted: includeCartonStatus ? row.diverted ?? 0 : undefined,
    palletised: includeCartonStatus ? row.palletised ?? 0 : undefined,
    totalILPNs: row.totalILPNs ?? ilpnIds.length,
  };
}

/**
 * @param {object} params
 * @param {AssignmentWorkspace} params.workspace
 * @param {Array} params.palletGroupsToAssign
 * @param {string[]} params.targetSpurKeys
 */
export async function assignPalletGroups(params) {
  // TODO: Replace with POST /inbound-planning/pallet-groups/assign
  const { workspace, palletGroupsToAssign, targetSpurKeys } = params;

  const result = applyAssignPalletGroupsToSpurs({
    assignmentsBySpur: workspace.spurAssignments,
    spurRows: workspace.spurSummary,
    palletGroupsToAssign,
    targetSpurKeys,
  });

  if (!result) {
    return { ok: false, error: "INVALID_ASSIGNMENT" };
  }

  const assignedIdSet = new Set(result.assignedIds);
  const nextWorkspace = {
    spurAssignments: result.assignmentsBySpur,
    spurSummary: result.spurRows,
    unassignedPalletGroups: workspace.unassignedPalletGroups.filter(
      (row) => !assignedIdSet.has(row.id),
    ),
  };

  return { ok: true, workspace: nextWorkspace, assignedIds: result.assignedIds };
}

/**
 * @param {object} params
 * @param {AssignmentWorkspace} params.workspace
 * @param {string} params.sourceSpurKey
 * @param {string} params.targetSpurKey
 * @param {string[]} params.pgIds
 */
export async function reassignPalletGroups(params) {
  // TODO: Replace with POST /inbound-planning/pallet-groups/reassign
  const { workspace, sourceSpurKey, targetSpurKey, pgIds } = params;

  const result = applyReassignToSpurs({
    assignmentsBySpur: workspace.spurAssignments,
    spurRows: workspace.spurSummary,
    sourceSpurKey,
    targetSpurKey,
    pgIds,
  });

  if (result.movedPgs.length === 0) {
    return { ok: false, error: "PG_NOT_FOUND_ON_SOURCE" };
  }

  return {
    ok: true,
    workspace: {
      spurAssignments: result.assignmentsBySpur,
      spurSummary: result.spurRows,
      unassignedPalletGroups: workspace.unassignedPalletGroups,
    },
    movedPgs: result.movedPgs,
  };
}

/**
 * @param {object} params
 * @param {AssignmentWorkspace} params.workspace
 * @param {string} params.sourceSpurKey
 * @param {string[]} params.pgIds
 */
export async function unassignPalletGroups(params) {
  // TODO: Replace with POST /inbound-planning/pallet-groups/unassign
  const { workspace, sourceSpurKey, pgIds } = params;

  const result = applyUnassignFromSpur({
    assignmentsBySpur: workspace.spurAssignments,
    spurRows: workspace.spurSummary,
    sourceSpurKey,
    pgIds,
    unassignedPalletGroups: workspace.unassignedPalletGroups,
  });

  if (result.unassignedIds.length === 0) {
    return { ok: false, error: "PG_NOT_FOUND_ON_SOURCE" };
  }

  return {
    ok: true,
    workspace: {
      spurAssignments: result.assignmentsBySpur,
      spurSummary: result.spurRows,
      unassignedPalletGroups: result.unassignedPalletGroups,
    },
    unassignedIds: result.unassignedIds,
  };
}

/**
 * @param {object} params
 * @param {AssignmentWorkspace} params.workspace
 * @param {string[]} params.spurKeys
 */
export async function completeSpurs(params) {
  // TODO: Replace with POST /inbound-planning/spurs/complete
  const { workspace, spurKeys } = params;

  const keySet = new Set(spurKeys);
  const spurSummary = workspace.spurSummary.map((row) => {
    if (!keySet.has(row.spur) || !row.readyToComplete) {
      return row;
    }
    return {
      ...row,
      spurStatus: "Idle",
      readyToComplete: false,
      palletGroup: "—",
      palletGroupCount: 0,
      inboundId: "—",
      shipTo: "—",
      totalCartons: 0,
      diverted: 0,
      palletised: 0,
    };
  });

  const spurAssignments = { ...workspace.spurAssignments };
  spurKeys.forEach((key) => {
    spurAssignments[key] = [];
  });

  return {
    ok: true,
    workspace: {
      ...workspace,
      spurSummary,
      spurAssignments,
    },
  };
}

/** @returns {AssignmentWorkspace} */
export function buildWorkspaceSnapshot({
  unassignedPalletGroups,
  spurSummary,
  spurAssignments,
}) {
  return cloneWorkspaceState({
    unassignedPalletGroups,
    spurSummary,
    spurAssignments,
  });
}

export function getApiErrorMessage(errorCode) {
  switch (errorCode) {
    case "INVALID_ASSIGNMENT":
      return "Could not assign — use one PG to many spurs, or many PGs to one spur.";
    case "PG_NOT_FOUND_ON_SOURCE":
      return "Selected pallet group(s) were not found on the source spur.";
    default:
      return "Something went wrong. Please try again.";
  }
}
